import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { InputProps } from "../Interfaces/InputProps";
import { InputType } from "../Enums/InputType";
import CallTableViewModel from "../../../ViewModels/CallTableViewModel";
import TranscriptFiltersViewModel from "./ViewModels/TranscriptFiltersViewModel";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { handleAutoCompleteChange } from "../Utility/FilterChangeHandlers"
import { useSnackbar } from "notistack";
import { TextFieldValidator } from "../Utility/InputValidation";

interface ITranscriptFiltersDialogProps {
    open: boolean
    setOpen: (value: boolean) => void
    callTableViewModel: CallTableViewModel
    setCallTableViewModel: (value: CallTableViewModel) => void
}

export default function TranscriptFiltersDialog({ open, setOpen, callTableViewModel, setCallTableViewModel }: ITranscriptFiltersDialogProps) {
    const [transcriptFilters, setTranscriptFilters] = useState<TranscriptFiltersViewModel>(new TranscriptFiltersViewModel());
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (open) {
            setTranscriptFilters(callTableViewModel.filters.transcriptFilters)
        }
    }, [open])

    useEffect(() => {
        setTranscriptFilters({
            ...transcriptFilters,
            transcriptOptions: '',
            searchMode: '',
            searchValues: ''
        });
    }, [transcriptFilters.transcriptMode])

    const transcriptModes = ['Transcript Search', 'Non-Transcript Search']
    const transcriptOptions = ['Agent Transcript Search', 'Customer Transcript Search']
    const searchMode = ['Exact Phrase', 'Any Word(s)', 'All Word(s)', 'Similar Word(s)',]

    const inputProps: InputProps[] = [
        {
            name: 'transcriptMode',
            label: 'Transcript Mode',
            value: transcriptFilters.transcriptMode,
            options: transcriptModes,
            type: InputType.AutoComplete
        },
        {
            name: 'transcriptOptions',
            label: 'Transcript Options',
            value: transcriptFilters.transcriptOptions,
            options: transcriptOptions,
            disabled: transcriptFilters.transcriptMode !== 'Transcript Search',
            type: InputType.AutoComplete
        },
        {
            name: 'searchMode',
            label: 'Search Mode',
            value: transcriptFilters.searchMode,
            options: searchMode,
            disabled: transcriptFilters.transcriptMode !== 'Transcript Search',
            type: InputType.AutoComplete
        },
        {
            name: 'searchValues',
            label: 'Search For',
            value: transcriptFilters.searchValues,
            disabled: transcriptFilters.transcriptMode !== 'Transcript Search',
            type: InputType.TextField
        },
    ];

    const getInput = (inputProps: InputProps) => {
        return (
            <Grid key={inputProps.name} size={12}>

                {
                    inputProps.type === InputType.AutoComplete && !inputProps.disabled &&
                    <>
                        <InputLabel>{inputProps.label}</InputLabel>
                        <Autocomplete
                            onChange={(_e: any, newValue: string) => handleAutoCompleteChange(inputProps.name, newValue, setTranscriptFilters)}
                            options={inputProps.options!}
                            value={inputProps.value as string}
                            fullWidth
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </>
                }
                {
                    inputProps.type === InputType.TextField && !inputProps.disabled &&
                    <>
                        <InputLabel>{inputProps.label}</InputLabel>
                        <TextField
                            name={inputProps.name}
                            onChange={handleChange}
                            value={inputProps.value}
                            fullWidth
                        />
                    </>
                }

            </Grid>)
    }

    const handleChange = (e: any) => {
        if (TextFieldValidator(e.target.value))
            setTranscriptFilters({
                ...transcriptFilters,
                [e.target.name]: e.target.value
            })
    }

    const confirm = () => {
        //ensure all the values are manditory
        if (transcriptFilters.transcriptMode !== '' && //must have a transcript value
            (transcriptFilters.transcriptMode !== 'Transcript Search' || //if is a non transcript
                (transcriptFilters.transcriptMode === 'Transcript Search' && //check transcript has all the required values
                    transcriptFilters.transcriptOptions !== '' &&
                    transcriptFilters.searchMode !== '' &&
                    transcriptFilters.searchValues !== ''
                ))) {

            const newCallTableViewModel = { ...callTableViewModel };

            newCallTableViewModel.filters.transcriptFilters = transcriptFilters;

            setCallTableViewModel({
                ...newCallTableViewModel
            })

            setOpen(false);
        } else {
            enqueueSnackbar("All values must be selected to confirm", { variant: 'warning' });
        }
    }

    return (
        <Dialog onClose={() => setOpen(false)} open={open} fullWidth maxWidth="sm">
            <DialogTitle>Transcript Filters</DialogTitle>
            <DialogContent>
                <Grid container height="100%">

                    {
                        inputProps.map(props => getInput(props))
                    }

                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)}>
                    Cancel
                </Button>
                <Button onClick={confirm}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    )
}